<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
  import {
    computed,
    watch,
    ref,
    onBeforeMount,
    onBeforeUnmount,
    useAttrs,
  } from 'vue';
  import {getComponentConfig} from '@core/utils/getComponentConfig';
  import type {StyleValue} from 'vue';

  interface Props {
    modelValue?: boolean;
    color?: string;
    timeout?: number;
    multiLine?: boolean;
    class?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    color: 'success',
    timeout: -1,
    multiLine: false,
    class: '',
  });

  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
  }>();

  const attrs = useAttrs();
  const snackbarStyle = computed<StyleValue>(() => attrs.style as StyleValue);

  const defaultConfig = {
    base: 'absolute flex items-center gap-4 px-4 py-3 rounded-lg shadow-lg transition-all duration-300 ease-in-out w-[500px] flex-row justify-between',
    colors: {
      success: 'bg-[var(--status-success)] text-white',
      error: 'bg-[var(--status-error)] text-white',
      warning: 'bg-[var(--status-warning)] text-white',
      info: 'bg-[var(--status-info)] text-white',
      primary:
        'bg-[rgb(var(--brand-primary))] text-white dark:bg-[rgb(var(--brand-primary))] dark:text-white',
      secondary:
        'bg-[rgb(var(--brand-secondary))] text-white dark:bg-[rgb(var(--brand-secondary))] dark:text-white',
      accent:
        'bg-[rgb(var(--brand-accent))] text-white dark:bg-[rgb(var(--brand-accent))] dark:text-white',
    },
  };

  const config = ref(defaultConfig);

  onBeforeMount(async () => {
    config.value = await getComponentConfig('Snackbar', defaultConfig);
  });

  const isVisible = computed({
    get: () => props.modelValue,
    set: value => emit('update:modelValue', value),
  });

  let hideTimerId: ReturnType<typeof setTimeout> | undefined;

  const clearHideTimer = () => {
    if (hideTimerId !== undefined) {
      clearTimeout(hideTimerId);
      hideTimerId = undefined;
    }
  };

  const snackbarClasses = computed(() => {
    const baseClasses = ['snackbar', config.value.base, props.class];
    const colorClass =
      config.value.colors[props.color as keyof typeof config.value.colors] ||
      config.value.colors.success;
    baseClasses.push(colorClass);

    if (props.multiLine) {
      baseClasses.push('flex-col items-start py-4');
    }

    return baseClasses.join(' ');
  });

  // Handle timeout: single timer, cleared on close/unmount
  watch(
    () => props.modelValue,
    newValue => {
      clearHideTimer();
      if (newValue && props.timeout > 0) {
        hideTimerId = setTimeout(() => {
          hideTimerId = undefined;
          isVisible.value = false;
        }, props.timeout);
      }
    },
    {immediate: true},
  );

  onBeforeUnmount(() => {
    clearHideTimer();
  });
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="translate-y-full opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition-all duration-300 ease-in"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-full opacity-0">
    <div
      v-if="isVisible"
      :class="snackbarClasses"
      :style="snackbarStyle">
      <div
        class="snackbar__content"
        :class="{'w-full': multiLine}">
        <slot />
      </div>
      <div
        v-if="$slots.actions"
        class="flex items-center ms-4 shrink-0"
        :class="{'mt-2 ms-0 w-full justify-end': multiLine}">
        <slot name="actions" />
      </div>
    </div>
  </Transition>
</template>
